import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Order, OrderHeader, Paged } from '@/features/orders/types';
import {
  api,
  ApiError,
  get,
  post,
  type ApiEnvelope,
} from '@/lib/api/client';
import { dayBounds, storeToday } from '@/lib/store-time';

/**
 * The store's third-party deliveries.
 *
 * These are the store order endpoints the rest of the portal already speaks
 * to, pinned to `DELIVERY` and narrowed by provider and zone — the same
 * space-separated tag string the global order search builds.
 *
 * Every key starts with `orders`, so the order actions sheet — which
 * invalidates that prefix after each change — refreshes these lists too.
 */

export interface DeliveryProvider {
  id: string;
  name: string | null;
}

export interface DeliveryZone {
  id: string;
  name: string | null;
}

/** `OrderDto` carries a bag count the global search never showed. */
export interface DeliveryOrder extends Order {
  bags?: number | null;
}

/**
 * The status the search is narrowed to. The values are the API's `OrderFilter`
 * enum, which travels in the URL.
 *
 * The original listed "Dispatched" and "Delivered" as two entries with the
 * same code. 10 is `Dispatched`, which for a delivery means delivered, so they
 * are one entry here.
 */
export const DELIVERY_STATUSES = [
  { value: '12', labelKey: 'deliveries.status.all' },
  { value: '0', labelKey: 'deliveries.status.pending' },
  { value: '22', labelKey: 'deliveries.status.assigned' },
  { value: '20', labelKey: 'deliveries.status.inTransit' },
  { value: '10', labelKey: 'deliveries.status.delivered' },
  { value: '11', labelKey: 'deliveries.status.cancelled' },
] as const;

export const DEFAULT_STATUS = '0';

export interface DeliveryFilters {
  /** Store calendar days, `yyyy-MM-dd`. */
  start: string;
  end: string;
  providerId: string;
  zoneId: string;
  status: string;
}

/** The store's current week, Sunday to Saturday — the original's default. */
export function currentWeek(): { start: string; end: string } {
  const start = new Date(`${storeToday()}T00:00:00.000Z`);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

function searchBody(filters: DeliveryFilters) {
  const { startDate, endDate } = dayBounds(filters.start, filters.end);
  return {
    FilterString: [
      'DELIVERY',
      filters.providerId ? `DPROVIDER-${filters.providerId}` : '',
      filters.zoneId ? `DZONE-${filters.zoneId}` : '',
    ]
      .filter(Boolean)
      .join(' '),
    StartDate: startDate,
    EndDate: endDate,
  };
}

/** Providers this account works for; a super admin is answered all of them. */
export function useDeliveryProviders(userId: string | null) {
  return useQuery({
    queryKey: ['delivery-providers', userId],
    queryFn: () =>
      get<Paged<DeliveryProvider>>(`tpd/providers/${userId}`).then(
        (page) => page?.results ?? []
      ),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
}

/** Active zones at the store's primary location, optionally one provider's. */
export function useDeliveryZones(
  locationId: string | null,
  providerId: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['delivery-zones', locationId, providerId],
    queryFn: () =>
      get<Paged<DeliveryZone>>(
        `deliveries/deliveryzones/active/${locationId}${
          providerId ? `/${providerId}` : ''
        }`
      ).then((page) => page?.results ?? []),
    enabled: enabled && Boolean(locationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeliverySearch({
  businessId,
  filters,
  term,
  page,
  size,
  sort,
  enabled,
}: {
  businessId: string | null;
  filters: DeliveryFilters;
  term: string;
  page: number;
  size: number;
  sort: 'asc' | 'desc';
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [
      'orders',
      'deliveries',
      'search',
      businessId,
      filters,
      term,
      page,
      size,
      sort,
    ],
    queryFn: async () => {
      // The total arrives in the envelope's `message`, beside the page — which
      // is why this reads the envelope itself instead of going through `post`.
      const { data: body } = await api.post<ApiEnvelope<Paged<DeliveryOrder>>>(
        `businesses/V2/${businessId}/orders/fast/${size}/${page}/${filters.status}`,
        {
          ...searchBody(filters),
          SearchString: term,
          // The API reads "present" as the instruction, so the other is null.
          OrderAsc: sort === 'asc' ? true : null,
          OrderDesc: sort === 'desc' ? true : null,
        }
      );
      if (body?.success === false) {
        throw new ApiError(
          body.error || body.message || 'The request was not accepted.'
        );
      }
      return {
        orders: body?.data?.results ?? [],
        count: Number.parseInt(body?.message ?? '', 10) || 0,
      };
    },
    enabled: enabled && Boolean(businessId),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Counts for the stat tiles: the same range, provider and zone, every status. */
export function useDeliveryHeader(
  businessId: string | null,
  filters: DeliveryFilters,
  enabled: boolean
) {
  const body = searchBody(filters);

  return useQuery({
    queryKey: ['orders', 'deliveries', 'header', businessId, body],
    queryFn: () =>
      post<OrderHeader>(`businesses/orderheader/${businessId}`, body),
    enabled: enabled && Boolean(businessId),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Every delivery matching the filters, as the spreadsheet the API builds. */
export function useExportDeliveries() {
  return useMutation({
    mutationFn: async ({
      businessId,
      filters,
      term,
      sort,
    }: {
      businessId: string;
      filters: DeliveryFilters;
      term: string;
      sort: 'asc' | 'desc';
    }) => {
      const response = await api.post<Blob>(
        `businesses/report/${businessId}/orders/${filters.status}`,
        {
          ...searchBody(filters),
          SearchString: term,
          OrderAsc: sort === 'asc' ? true : null,
          OrderDesc: sort === 'desc' ? true : null,
          All: true,
        },
        { responseType: 'blob' }
      );
      const blob = response.data;
      // A JSON body here is the error envelope, not a workbook.
      if (blob.type.includes('json')) {
        const body = JSON.parse(await blob.text()) as ApiEnvelope<unknown>;
        throw new ApiError(
          body.error || body.message || 'The request was not accepted.'
        );
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `deliveries_${filters.start}_${filters.end}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });
}

/**
 * A zone's open deliveries, in route order.
 *
 * An empty zone answers a message and no payload, so `null` is "nothing to
 * sort", not a failure.
 */
export function useZoneRoute(zoneId: string) {
  return useQuery({
    queryKey: ['orders', 'deliveries', 'route', zoneId],
    queryFn: () =>
      get<Paged<DeliveryOrder> | null>(
        `deliveries/orders/sorting/${zoneId}`
      ).then((page) => page?.results ?? []),
    enabled: Boolean(zoneId),
  });
}

/** Saves the route: each order's position in the list is its stop number. */
export function useSaveRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderIds: string[]) =>
      post<unknown>(
        'deliveries/orders/sorting/update',
        orderIds.map((orderId, routeOrder) => ({ orderId, routeOrder }))
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['orders', 'deliveries'] }),
  });
}
